import { generateId } from "@msuite/utils/strings";
import type { Editor } from "@tiptap/react";
import { FloatingMenu } from "@tiptap/react/menus";
import clsx from "clsx";
import { useMemo } from "react";
import { Button, ButtonGroup } from "../../button";
import { Icon, type IconType, icon } from "../../icons";
import { useTranslation } from "../../localization";
import { Menu } from "../../menu";
import { Popover } from "../../popover";
import { Tooltip } from "../../tooltip";
import { cn } from "../../utils";

/** Props Interface */
interface EditorMenuProps {
	editor: Editor | null;
	menuType?: "static" | "floating";
}

interface EditorMenuButton {
	Icon: IconType;
	label: string;
	onClick: () => void;
	isActive?: boolean;
	isDisabled?: boolean;
	cyId?: string;
}

const textColors: string[] = [
	"--color-black",
	"--color-red-500",
	"--color-yellow-500",
	"--color-blue-500",
	"--color-emerald-500",
	"--color-teal-500",
	"--color-violet-500",
];

export function EditorMenu({ editor, menuType }: EditorMenuProps) {
	/** Guard */
	if (!editor) return null;
	const menuButtons = getMenuButtons(editor);

	/** Hooks */
	const { t } = useTranslation();

	const Wrapper = useMemo(() => {
		if (menuType === "floating") {
			return FloatingMenu;
		}
		return "div";
	}, [menuType]);

	/** Render */
	return (
		<Wrapper
			pluginKey={generateId()}
			editor={editor}
			options={{
				placement: "top-start",
				shift: true,
				flip: true,
			}}
			shouldShow={() => true}
		>
			<div
				className={cn(
					"h-stack flex-wrap sticky top-0 z-1",
					menuType === "floating" ? "w-max card" : "",
				)}
			>
				<Popover
					trigger="click"
					withCloseButton={false}
					variant="plain"
					render={(onClose) => (
						<div className="flex flex-row flex-wrap gap-2 min-w-[250px] card px-2">
							{textColors.map((color) => (
								<div
									className="aspect-square w-8 rounded-full cursor-pointer hover:opacity-80"
									onClick={() => {
										const root = document.documentElement;
										const colorValue = getComputedStyle(root).getPropertyValue(color).trim();
										editor.chain().focus().setColor(colorValue).run();
										onClose();
									}}
									key={color}
									style={{
										backgroundColor: `var(${color})`,
									}}
								/>
							))}
						</div>
					)}
				>
					<Button
						icon={
							<div
								className="w-3 aspect-square rounded-full"
								style={{
									backgroundColor: editor.getAttributes("textStyle").color || "black",
								}}
							/>
						}
						className="overflow-hidden border-[0.5px]"
					/>
				</Popover>
				{menuButtons.map((group, groupIndex) => (
					// The frame sits in a negative margin of its own width, so a group is exactly one
					// button tall in layout, like the lone buttons beside it. Without that the toolbar
					// stood a pixel taller than a row of buttons, and a header that swaps one for the
					// other moved everything beneath it by that pixel.
					<ButtonGroup
						key={`editor-menu-group-${groupIndex}`}
						className="-my-[0.5px] border-[0.5px] border-color rounded-lg w-max overflow-hidden"
					>
						{group.map(({ Icon, ...props }, buttonIndex) => (
							<Tooltip
								label={props.label}
								key={`editor-menu-tooltip-${groupIndex}-${buttonIndex}`}
								openDelay={100}
							>
								<Button
									variant="white"
									key={`editor-menu-button-${groupIndex}-${buttonIndex}`}
									icon={<Icon className={clsx(props.isActive ? "stroke-accent-500" : "")} />}
									onClick={() => {
										props.onClick();
									}}
									isDisabled={props.isDisabled}
									className="border-none rounded-none"
									data-cy={props.cyId}
								/>
							</Tooltip>
						))}
					</ButtonGroup>
				))}
				<Menu
					items={() => [
						{
							icon: icon("Table"),
							label: t("tables.insert_table"),
							onClick: () => {
								editor.chain().focus().insertTable({ cols: 3, rows: 3 }).run();
							},
						},
						"divider",
						{
							icon: icon("BetweenVerticalEnd"),
							isDisabled: !editor.can().addColumnAfter(),
							label: t("tables.insert_column_right"),
							onClick: () => {
								editor.chain().focus().addColumnAfter().run();
							},
						},
						{
							icon: icon("BetweenVerticalEnd"),
							isDisabled: !editor.can().addColumnBefore(),
							label: t("tables.insert_column_left"),
							onClick: () => {
								editor.chain().focus().addColumnBefore().run();
							},
						},
						{
							icon: icon("Columns2"),
							isDanger: true,
							isDisabled: !editor.can().deleteColumn(),
							label: t("tables.remove_column"),
							onClick: () => {
								editor.chain().focus().deleteColumn().run();
							},
						},
						"divider",
						{
							icon: icon("BetweenHorizontalStart"),
							isDisabled: !editor.can().addRowBefore(),
							label: t("tables.insert_row_above"),
							onClick: () => {
								editor.chain().focus().addRowBefore().run();
							},
						},
						{
							icon: icon("BetweenHorizontalStart"),
							isDisabled: !editor.can().addRowAfter(),
							label: t("tables.insert_row_below"),
							onClick: () => {
								editor.chain().focus().addRowAfter().run();
							},
						},
						{
							icon: icon("Rows2"),
							isDanger: true,
							isDisabled: !editor.can().deleteRow(),
							label: t("tables.remove_row"),
							onClick: () => {
								editor.chain().focus().deleteRow().run();
							},
						},
						"divider",
						{
							icon: icon("TableCellsMerge"),
							isDisabled: !editor.can().mergeCells(),
							label: t("tables.merge_cells"),
							onClick: () => {
								if (editor.can().mergeCells()) {
									editor.chain().focus().mergeCells().run();
								}
							},
						},
						{
							icon: icon("TableCellsSplit"),
							isDisabled: !editor.can().splitCell(),
							label: t("tables.split_cells"),
							onClick: () => {
								if (editor.can().splitCell()) {
									editor.chain().focus().splitCell().run();
								}
							},
						},
					]}
				>
					<Button
						icon={<Icon name="Table" />}
						className="border-[0.5px]"
					/>
				</Menu>
			</div>
		</Wrapper>
	);
}

function getMenuButtons(editor: Editor): Array<Array<EditorMenuButton>> {
	return [
		[
			{
				Icon: icon("Bold"),
				isActive: editor.isActive("bold"),
				label: "Bold",
				onClick: () => editor.chain().focus().toggleBold().run(),
				cyId: "text-bold",
			},
			{
				Icon: icon("Italic"),
				isActive: editor.isActive("italic"),
				label: "Italic",
				onClick: () => editor.chain().focus().toggleItalic().run(),
				cyId: "text-italic",
			},
			{
				Icon: icon("Strikethrough"),
				isActive: editor.isActive("strike"),
				label: "Strikethrough",
				onClick: () => editor.chain().focus().toggleStrike().run(),
				cyId: "text-strike",
			},
			{
				Icon: icon("Underline"),
				isActive: editor.isActive("underline"),
				label: "Underline",
				onClick: () => editor.chain().focus().toggleUnderline().run(),
				cyId: "text-underline",
			},
		],
		[
			{
				Icon: icon("AlignLeft"),
				isActive: editor.isActive({ textAlign: "left" }),
				label: "Align Left",
				onClick: () => editor.chain().focus().setTextAlign("left").run(),
				cyId: "text-align-left",
			},
			{
				Icon: icon("AlignCenter"),
				isActive: editor.isActive({ textAlign: "center" }),
				label: "Align Center",
				onClick: () => editor.chain().focus().setTextAlign("center").run(),
				cyId: "text-align-center",
			},
			{
				Icon: icon("AlignRight"),
				isActive: editor.isActive({ textAlign: "right" }),
				label: "Align Right",
				onClick: () => editor.chain().focus().setTextAlign("right").run(),
				cyId: "text-align-right",
			},
			{
				Icon: icon("AlignJustify"),
				isActive: editor.isActive({ textAlign: "justify" }),
				label: "Align Justified",
				onClick: () => editor.chain().focus().setTextAlign("justify").run(),
				cyId: "text-align-justify",
			},
		],
		[
			{
				Icon: icon("List"),
				isActive: editor.isActive("bulletList"),
				label: "Bullet List",
				onClick: () => editor.chain().focus().toggleBulletList().run(),
				cyId: "text-bullet-list",
			},
			{
				Icon: icon("ListOrdered"),
				isActive: editor.isActive("orderedList"),
				label: "Ordered List",
				onClick: () => editor.chain().focus().toggleOrderedList().run(),
				cyId: "text-ordered-list",
			},
		],
		[
			{
				Icon: icon("Text"),
				isActive: editor.isActive("paragraph"),
				label: "Paragraph",
				onClick: () => editor.chain().focus().setParagraph().run(),
				cyId: "text-paragraph",
			},
			{
				Icon: icon("Heading1"),
				isActive: editor.isActive("heading", { level: 1 }),
				label: "Heading 1",
				onClick: () => editor.chain().focus().setHeading({ level: 1 }).run(),
				cyId: "text-heading-1",
			},
			{
				Icon: icon("Heading2"),
				isActive: editor.isActive("heading", { level: 2 }),
				label: "Heading 2",
				onClick: () => editor.chain().focus().setHeading({ level: 2 }).run(),
				cyId: "text-heading-2",
			},
			{
				Icon: icon("Heading3"),
				isActive: editor.isActive("heading", { level: 3 }),
				label: "Heading 3",
				onClick: () => editor.chain().focus().setHeading({ level: 3 }).run(),
				cyId: "text-heading-3",
			},
			{
				Icon: icon("Heading4"),
				isActive: editor.isActive("heading", { level: 4 }),
				label: "Heading 4",
				onClick: () => editor.chain().focus().setHeading({ level: 4 }).run(),
				cyId: "text-heading-4",
			},
			{
				Icon: icon("Heading5"),
				isActive: editor.isActive("heading", { level: 5 }),
				label: "Heading 5",
				onClick: () => editor.chain().focus().setHeading({ level: 5 }).run(),
				cyId: "text-heading-5",
			},
		],
	];
}
